import React, { useState } from "react";
import ApiClient from "utils/ApiClient";

import {
  Typography,
  Grid,
  CircularProgress,
  TextField,
  Icon,
  IconButton,
  Chip,
  Tooltip
} from "@material-ui/core";
import SelectMultiLevel from "../../../common/components/input/Menu/SelectMultiLevel";
import { observer, inject } from "mobx-react";
import RecipientsButton from "./RecipientsButton";
import SettingsButton from "./SettingsButton";
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  chipSize: {
    fontSize: 10,
    height: "18px"
  },
  notificationWidth: {
    minWidth: "156px",
    maxWidth: "156px"
  },
  remindersWidth:{
    minWidth: "100px",
    maxWidth: "100px"
  },
  textFieldWidth:{
    minWidth: "53px",
    maxWidth: "53px"
  }
}));

const UNITS_VALUES = [
  {
    text: "mins",
    value: "1",
  },
  {
    text: "hours",
    value: "2",
  },
  {
    text: "days",
    value: "3",
  },
  {
    text: "months",
    value: "4",
  },
];

const OPERATION_VALUES = [
  { name: "before", value: "before" },
  { name: "after", value: "after" },
];

const EVENTS = [
  { name: "New row is added", value: "onRowAdd" },
  { name: "Row is edited", value: "onRowEdit" },
  { name: "Row is deleted", value: "onRowDelete" },
  { name: "Column is updated", value: "onCellCreateUpdate" },
];

function Reminder({ store, reminder, reminderKey, ...props }) {
  const classes = useStyles();
  const count = reminder.event_data?.count;
  const operation = reminder.event_data?.operation;
  const unitsValue = reminder.event_data?.units;
  const event = reminder.event;
  const isReminderSaved = !!reminder.id;
  const column =
    store.dateColumns.find((col) => col.id == reminder.event_data?.column_id) ||
    {};
  const isReminderSaving = store.savingReminders.has(
    reminder.id || "new_reminder"
  );

  const saveData = (reminder, doSave = true) => {
    if (
      doSave &&
      reminder.event === "onCellCreateUpdate" &&
      !reminder.column_id
    ) {
      return;
    }

    if (
      doSave &&
      (reminder.event !== "scheduled" ||
        (reminder.event_data.count_type &&
          reminder.event_data.count &&
          reminder.event_data.column_id &&
          reminder.event_data.units))
    ) {
      store.updateReminder(reminder);
    }
  };

  const onEventDataChange = (key, value, doSave = true) => {
    reminder.event_data[key] = value;
    saveData(reminder, doSave);
  };

  const onChange = (key, value, doSave = true) => {
    reminder[key] = value;
    const config = store.appStore.configStore.config;
    if (key === "event") {
      switch (value) {
        case "onRowAdd":
          reminder.data.mail_subject = `Notification from ${config.site.title} ( Row is Added )`;
          break;
        case "onRowEdit":
          reminder.data.mail_subject = `Notification from ${config.site.title} ( Row is Edited )`;
          break;
        case "onRowDelete":
          reminder.data.mail_subject = `Notification from ${config.site.title} ( Row is Deleted )`;
          break;
        case "onCellCreateUpdate":
          reminder.data.mail_subject = `Notification from ${config.site.title} ( Column Changed )`;
          break;
        default:
          reminder.data.mail_subject = `Notification from ${config.site.title}`;
          break;
      }
    }
    saveData(reminder, doSave);
  };

  const initialPausedVal = reminder.is_paused;
  const [ isPaused, setIsPaused ] = useState( isFinite(initialPausedVal) ? !!+initialPausedVal : false );
  //  !!+initialPausedVal == Boolean(Number(initialPausedVal))
  const [ isLoading, setIsLoading ] = useState(false);
  
  const pauseResumeHandler = async () => {
    setIsLoading(true);
    try{
      let resp;
      if(isPaused == false){
        resp = await ApiClient.EventHandlers.pauseHandler({
          list_id: reminder.list_id,
          handler_id: reminder.id
        });
      }
      else{
        resp = await ApiClient.EventHandlers.resumeHandler({
          list_id: reminder.list_id,
          handler_id: reminder.id
        });
      }
      if(resp.success){
        setIsPaused(prev=>!prev);
        window.appStore.notificationStore.showNotification(
          resp.msg ? resp.msg : `Trigger ${isPaused ? "Paused" : "Resumed"} successfully` ,
          "success"
          );
      }
      else{
        window.appStore.notificationStore.showNotification(
          resp.msg ? resp.msg : "Something went wrong" ,
          "error"
          );
      }
      setIsLoading(false);
    } catch(e) {
      setIsLoading(false);
      window.appStore.notificationStore.showNotification(
        e.response?.msg ? e.response?.msg : "Something went wrong" ,
        "error"
        );
    }
  }
        
  return (
    <Grid item>
      <Grid container justify="flex-start" spacing={1} alignItems="center" wrap="nowrap" className={isPaused && "pausedNotification"}>
        {event === "scheduled" ? (
          <>
            <Grid item>
              <Typography variant="body">Remind</Typography>
            </Grid>
            <Grid item xs={1}>
              <TextField
                className={classes.textFieldWidth}
                type="number"
                value={count}
                variant="outlined"
                onBlur={(e) => {
                  onEventDataChange("count", e.target.value, true);
                }}
                onChange={(e) => {
                  if (e.target.value <= 0) {
                    return;
                  }
                  onEventDataChange("count", e.target.value, false);
                }}
              />
            </Grid>
            <Grid item xs={2} className={classes.remindersWidth}>
              <SelectMultiLevel
                className="select_multilevel"
                values={UNITS_VALUES}
                valueKey={"value"}
                displayKey={"text"}
                value={UNITS_VALUES.find((item) => item.value == unitsValue)}
                // label={"units"}
                onChange={(val) => {
                  onEventDataChange("units", val.value + "");
                }}
              />
            </Grid>
            <Grid item xs={2} className={classes.remindersWidth}>
              <SelectMultiLevel
                className="select_multilevel"
                values={OPERATION_VALUES}
                valueKey={"value"}
                displayKey={"name"}
                value={OPERATION_VALUES.find((item) => item.value == operation)}
                // label={"operation"}
                onChange={(val) => {
                  onEventDataChange("operation", val.value);
                }}
              />
            </Grid>
            <Grid item xs={3} className={classes.notificationWidth}>
              <SelectMultiLevel
                className="select_multilevel"
                values={store.dateColumns}
                valueKey={"alias_path"}
                displayKey={"name"}
                value={column}
                label={"column"}
                onChange={(val) => {
                  onEventDataChange("column_id", val.id);
                }}
              />
            </Grid>
          </>
        ) : (
          <>
            <Grid item>
              <Typography variant="body">When</Typography>
            </Grid>
            <Grid item xs={4} className={classes.notificationWidth}>
              <SelectMultiLevel
                className="select_multilevel"
                values={EVENTS}
                valueKey={"value"}
                displayKey={"name"}
                value={EVENTS.find((item) => item.value == event)}
                // label={"units"}
                onChange={(val) => {
                  onChange("event", val.value + "");
                }}
              />
            </Grid>
            <Grid item xs={3}>
              {event === "onCellCreateUpdate" && (
                <SelectMultiLevel
                  className="select_multilevel"
                  values={store.listColumns}
                  valueKey={"alias_path"}
                  displayKey={"name"}
                  value={
                    store.listColumns.find(
                      (col) => col.id === reminder.column_id
                    ) || {}
                  }
                  label={"column"}
                  tooltip="on which column change"
                  onChange={(val) => {
                    onChange("column_id", val.id);
                  }}
                />
              )}
            </Grid>
          </>
        )}
        {isReminderSaved && (
          <>
          <Chip label="paused" color="primary" className={[classes.chipSize,"pausedChip"]} size="small" 
            style={{ visibility: !isPaused && "hidden" }} />
            <React.Fragment>
              <RecipientsButton
                isDefaultOpen={store.lastCreatedReminderId === reminder.id}
                store={store}
                reminder={reminder}
                onChange={() => saveData(reminder)}
                />
              <SettingsButton
                store={store}
                reminder={reminder}
                reminderKey={reminderKey}
                onChange={() => saveData(reminder)}
                />
            </React.Fragment>
          </>
        )}
        <Grid item>
          <Tooltip title={isPaused ? "resume" : "pause"} arrow>
          <IconButton
            aria-label="pausing a trigger"
            onClick={ pauseResumeHandler }
          >
            { isReminderSaved && !isReminderSaving && (
              isLoading ? <CircularProgress size={13} /> : isPaused ? <Icon className="fas fa-play" /> : 
                <Icon className="fas fa-pause" />
            )}
          </IconButton> 
          </Tooltip>
        </Grid>
        <Grid item>
          <IconButton
            aria-label="users"
            onClick={() => store.deleteReminder(reminderKey)}
          >
            {isReminderSaved && !isReminderSaving && (
              <Icon className="fas fa-trash-alt" />
            )}
            {isReminderSaving && <CircularProgress size={20} />}
          </IconButton>
        </Grid>
      </Grid>
    </Grid>
  );
}

export default observer(Reminder);
